import { wrapApiCall } from '#~/api/index.js';
import { nats } from '#~/nats/index.js';
import {
  BridgeDiscoveryRegistryLinkingAuthorizationRequest,
  BridgeDiscoveryRegistryLinkingAuthorizationVerifyRequest,
  type BridgeDiscoveryRegistryLinkingAuthorizationResponse
} from '@tryiris-ai/common.protojs/api/v1/bridge_discovery_registry';

import {
  BridgeLinkingAuthorizationRequestCreate,
  BridgeLinkingAuthorizationRequestVerify
} from '@tryiris-ai/mailroom-helper/bridgeDiscoveryRegistry';
import type { OmitResponseProps } from '../types.js';

/**
 * Creates a bridge discovery linking authorization request.
 *
 * @param token - Access token.
 * @param requestId - Request ID.
 * @returns Bridge discovery linking authorization response.
 */
export const createBridgeDiscoveryLinkingAuthRequest = async ({
  token,
  requestId
}: {
  token: string;
  requestId: string;
}): Promise<OmitResponseProps<BridgeDiscoveryRegistryLinkingAuthorizationResponse>> => {
  const response = await wrapApiCall(async () =>
    BridgeLinkingAuthorizationRequestCreate.request(
      nats.cloudConnection(),
      BridgeDiscoveryRegistryLinkingAuthorizationRequest.fromPartial({
        token,
        requestId
      })
    )
  );

  return response;
};

/**
 * Verifies a bridge discovery linking authorization request.
 *
 * @param token - Access token.
 * @param requestId - Request ID.
 * @returns Whether the same user is requesting the link.
 */
export const verifyBridgeDiscoveryLinkingAuthRequest = async ({
  token,
  requestId
}: {
  token: string;
  requestId: string;
}): Promise<boolean> => {
  const response = await wrapApiCall(async () =>
    BridgeLinkingAuthorizationRequestVerify.request(
      nats.cloudConnection(),
      BridgeDiscoveryRegistryLinkingAuthorizationVerifyRequest.fromPartial({
        token,
        requestId
      })
    )
  );

  return response.isSameUserRequesting;
};
